/**
 * Portal end-user auth (ADR-0010 D6 · P7b2) — a realm SEPARATE from the
 * operator auth in `lib/auth.ts`.
 *
 * Portal users (`app_users`) carry a JWT with `realm='app_user'`; their
 * session is persisted under a distinct localStorage key so the two realms
 * never collide. The identity is global (one account, many apps), so the
 * store holds only `{token, user}` — the per-app role is resolved per-page
 * from the page/me response.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * URL prefix for every portal SPA route. The app serves from ROOT now (it used to ride a
 * `/` base path), so the end-user portal lives at `/portal/...`. Build every portal
 * path from these helpers so the prefix stays in one place.
 */
export const PORTAL_PREFIX = "/portal";

/**
 * The app's ENTRY path (`…/portal/:slug`) — the bare app root. It resolves to
 * the app's real home page (the `is_home` page, whose slug is arbitrary, e.g.
 * "dashboard") via `PortalAppEntry`. Never hard-code `/p/home`: not every app
 * names its home "home".
 */
export const portalHomePath = (appSlug: string): string => `${PORTAL_PREFIX}/${appSlug}`;

/** A specific page (`…/portal/:slug/p/:pageSlug`). */
export const portalPagePath = (appSlug: string, pageSlug: string): string =>
  `${PORTAL_PREFIX}/${appSlug}/p/${pageSlug}`;

/** A readable DETAIL URL: the page + the row id as a PATH segment
 *  (`…/p/:pageSlug/:rowId`), the clean REST twin of the legacy `?row=<id>` query.
 *  `PortalPageView` seeds `$page.row` from the segment; a row-action's
 *  `open_mode:"page"` builds this (was `portalPagePath(...) + "?row=" + id`). */
export const portalRowPath = (appSlug: string, pageSlug: string, rowId: string): string =>
  `${portalPagePath(appSlug, pageSlug)}/${encodeURIComponent(rowId)}`;

/** A standalone assistant surface (ADR-0049): the autonomous full-page chat
 *  (`…/portal/:slug/i/chat`) or voice (`…/portal/:slug/i/voice`) interface. The
 *  `/i/` ("interface") prefix namespaces the standalone interface surfaces —
 *  future-proof for other interface kinds (bot, widget) getting their own URL. */
export const portalAssistantPath = (appSlug: string, surface: "chat" | "voice"): string =>
  `${PORTAL_PREFIX}/${appSlug}/i/${surface}`;

/** The portal (slug-less) login — the tenant-branded front door for
 *  "one login, many apps" (ADR-0015). Optionally carries a post-login `next`
 *  target (e.g. a deep-linked app), else the launcher. */
export const parentLoginPath = (next?: string): string =>
  `${PORTAL_PREFIX}/login${next ? `?next=${encodeURIComponent(next)}` : ""}`;

/** The PARENT portal launcher (the app grid). */
export const parentLauncherPath = (): string => `${PORTAL_PREFIX}/me`;

/** The single self-signup page (ADR-0016) — account-only registration. */
export const parentSignupPath = (): string => `${PORTAL_PREFIX}/signup`;

/** The end-user's own account page (profile / password / 2FA) — slug-less,
 *  reached from the in-app avatar popover (ADR-0017). */
export const parentAccountPath = (): string => `${PORTAL_PREFIX}/account`;

export interface PortalUser {
  id: string;
  email: string;
  full_name?: string | null;
}

interface PortalAuthState {
  token: string | null;
  /** Opaque rotated refresh token — renews the short access token silently so the
   *  portal session doesn't just expire (aligned with the operator realm). */
  refreshToken: string | null;
  user: PortalUser | null;
  setSession: (token: string, user: PortalUser, refreshToken?: string | null) => void;
  logout: () => void;
}

export const usePortalAuth = create<PortalAuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      setSession: (token, user, refreshToken = null) => set({ token, user, refreshToken }),
      logout: () => set({ token: null, user: null, refreshToken: null }),
    }),
    { name: "tray-portal-auth" },
  ),
);
